// App.js – Admin Login First, Website at /website
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';

import Website from './pages/Website';
import Admin from './pages/Admin';
import AdminLogin from './pages/AdminLogin';
import StaffPanel from './pages/StaffPanel';
import LoginPage from './pages/LoginPage';
import CategoryPage from './pages/CategoryPage';
import AllCategoriesPage from './pages/AllCategoriesPage';
import DecorationEnquiryPage from './components/website/DecorationEnquiryPage';
import AllProductsPage from './pages/AllProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './components/website/CheckoutPage';
import ProfilePage from './components/website/ProfilePage';
import DecorationGalleryPage from './pages/DecorationGalleryPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';

// Protected Route Wrapper for Admin
const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#fff5f5'
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #dc2626',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }}
        />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Protected Route Wrapper for Staff
const StaffRoute = ({ children }) => {
  const { user, isStaff, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#fff5f5'
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #dc2626',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }}
        />
      </div>
    );
  }

  if (!user || (!isStaff && !isAdmin)) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Back Button Handler
const BackButtonHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const exitAppTimer = useRef(null);
  const isFirstBackPress = useRef(true);

  useEffect(() => {
    let backButtonListener;

    const handleBackButton = async () => {
      const currentPath = location.pathname;
      const canGoBack = window.history.length > 1;
      
      if (canGoBack) {
        navigate(-1);
        isFirstBackPress.current = true;
        if (exitAppTimer.current) clearTimeout(exitAppTimer.current);
        return;
      }
      
      if (isFirstBackPress.current) {
        isFirstBackPress.current = false;
        console.log('Press back again to exit');
        if (exitAppTimer.current) clearTimeout(exitAppTimer.current);
        exitAppTimer.current = setTimeout(() => {
          isFirstBackPress.current = true;
        }, 2000);
      } else {
        if (exitAppTimer.current) clearTimeout(exitAppTimer.current);
        await CapacitorApp.exitApp();
      }
    };

    const setupBackButton = async () => {
      try {
        if (CapacitorApp && typeof CapacitorApp.addListener === 'function') {
          backButtonListener = await CapacitorApp.addListener('backButton', handleBackButton);
          console.log('Back button listener registered successfully');
        } else {
          window.addEventListener('popstate', () => {
            if (window.history.length <= 1 && window.confirm('Do you want to exit?')) {
              window.close();
            }
          });
        }
      } catch (error) {
        console.error('Error setting up back button:', error);
        window.addEventListener('popstate', () => {
          if (window.history.length <= 1 && window.confirm('Do you want to exit?')) {
            window.close();
          }
        });
      }
    };

    setupBackButton();

    return () => {
      if (backButtonListener) backButtonListener.remove();
      if (exitAppTimer.current) clearTimeout(exitAppTimer.current);
    };
  }, [navigate, location]);

  return null;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <BackButtonHandler />
          <Routes>
            {/* PUBLIC ROUTES - No authentication required */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            
            {/* Root - Admin Login First */}
            <Route path="/" element={<Navigate to="/admin/login" replace />} />
            
            {/* Admin Login */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Website - All website routes */}
            <Route path="/website" element={<Website />} />
            <Route path="/website/login" element={<LoginPage />} />
            <Route path="/website/checkout" element={<CheckoutPage />} />
            <Route path="/website/profile" element={<ProfilePage onClose={() => {}} />} />
            <Route path="/website/category/:categoryId" element={<CategoryPage />} />
            <Route path="/website/all-categories" element={<AllCategoriesPage />} />
            <Route path="/website/decoration-enquiry" element={<DecorationEnquiryPage />} />
            <Route path="/website/decoration-gallery" element={<DecorationGalleryPage />} />
            <Route path="/website/decoration-gallery/category/:categoryId" element={<DecorationGalleryPage />} />
            <Route path="/website/decoration-gallery/item/:itemId" element={<DecorationGalleryPage />} />
            <Route path="/website/products/:sectionId" element={<AllProductsPage />} />
            <Route path="/website/product/:productId" element={<ProductDetailPage />} />

            {/* Admin - Protected */}
            <Route path="/admin/*" element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            } />

            {/* Staff - Protected */}
            <Route path="/staff/*" element={
              <StaffRoute>
                <StaffPanel />
              </StaffRoute>
            } />
            
            {/* 404 Catch All */}
            <Route path="*" element={<Navigate to="/admin/login" replace />} />
          </Routes>
          
          <ToastContainer
            position="top-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            style={{ marginTop: '50px' }}
          />
        </BrowserRouter>
      </CartProvider>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Remove blue highlight on click for all elements */
        * {
          -webkit-tap-highlight-color: transparent !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }

        /* Allow text selection where needed */
        input, textarea, select, [contenteditable="true"] {
          -webkit-user-select: text !important;
          user-select: text !important;
          -webkit-tap-highlight-color: rgba(220, 38, 38, 0.1) !important;
        }

        /* Remove focus outline for clickable elements */
        button, a, [role="button"], .clickable, .product-card, .category-card {
          outline: none !important;
          -webkit-focus-ring-color: transparent !important;
        }

        /* For links */
        a {
          -webkit-tap-highlight-color: transparent !important;
          text-decoration: none;
        }

        /* For product cards and clickable divs */
        .product-card, .category-card, [onClick], [role="button"], 
        .clickable, .cursor-pointer {
          cursor: pointer;
          -webkit-tap-highlight-color: transparent !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }

        /* Remove blue glow on focus */
        :focus {
          outline: none !important;
        }

        /* For touch devices */
        @media (hover: none) and (pointer: coarse) {
          * {
            -webkit-tap-highlight-color: transparent !important;
          }
          
          button, a, [onClick], .clickable {
            -webkit-tap-highlight-color: transparent !important;
          }
        }

        /* For images */
        img {
          -webkit-user-drag: none !important;
          user-drag: none !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }

        /* Remove blue highlight for webkit browsers */
        ::selection {
          background: transparent !important;
        }

        /* Keep selection for text in inputs */
        input::selection, textarea::selection {
          background: rgba(220, 38, 38, 0.2) !important;
          color: #1a0a0a !important;
        }

        /* For accessibility - style focus-visible nicely */
        :focus-visible {
          outline: 2px solid #dc2626 !important;
          outline-offset: 2px !important;
        }
        
        .Toastify__toast {
          border-radius: 12px !important;
          font-family: 'Inter', sans-serif !important;
        }
        
        .Toastify__toast--success {
          background: #dc2626 !important;
          color: white !important;
        }
        
        .Toastify__toast--error {
          background: #ef4444 !important;
          color: white !important;
        }
        
        body.modal-open {
          overflow: hidden;
        }
      `}</style>
    </AuthProvider>
  );
}

export default App;